import type {
  AccountingFrame,
  GameStateSnapshot,
  RankExpPoint,
  RankSubmissionDraft,
} from '@/domain/types'
import { matchRankJob } from './rank-job'

export const RANK_PRESET_ID = 'mxdc' as const
export const RANK_MINIMUM_DURATION_MS = 30 * 60 * 1_000
export const RANK_MAP_FRESHNESS_MS = 15_000
export const RANK_RECOGNITION_GRACE_MS = 120_000
export const RANK_MAXIMUM_SEGMENT_MS = 7 * 24 * 60 * 60 * 1_000

interface ExpAccumulator {
  start: RankExpPoint | null
  end: RankExpPoint | null
  last: RankExpPoint | null
  startedAt: number | null
  endedAt: number | null
  gain: number
  trustedDurationMs: number
}

interface ActiveSegment {
  mapName: string
  levelBucket: number
  lastContinuousAt: number
  lastMapObservedAt: number
  recognitionPaused: boolean
  captureGeneration: number
  jobName: string | null
  exp: ExpAccumulator
}

const emptyExp = (): ExpAccumulator => ({
  start: null,
  end: null,
  last: null,
  startedAt: null,
  endedAt: null,
  gain: 0,
  trustedDurationMs: 0,
})

export function rankLevelBucket(level: number): number {
  return Math.floor(level / 5) * 5
}

export function normalizeRankMapName(value: string): string {
  return value
    .normalize('NFKC')
    .replace(/[\u0000-\u001f\u007f-\u009f]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
}

export class EfficiencySegmentTracker {
  private segment: ActiveSegment | null = null

  constructor(private readonly presetVersion: number) {}

  reset(): void {
    this.segment = null
  }

  suspend(): void {
    if (!this.segment) return
    this.segment.recognitionPaused = true
    this.segment.exp.last = null
  }

  ingest(state: GameStateSnapshot, accounting: AccountingFrame): RankSubmissionDraft | null {
    const mapName = state.mapName ? normalizeRankMapName(state.mapName) : ''
    const mapObservedAt = state.observedAt?.mapName ?? null
    const levelBucket = state.level === null ? null : rankLevelBucket(state.level)

    if (!this.segment) {
      if (!this.mapIsFresh(state.capturedAt, mapName, mapObservedAt) || levelBucket === null) return null
      this.startSegment(state, accounting, mapName, levelBucket, mapObservedAt!)
      this.observeExp(accounting)
      return null
    }

    const segment = this.segment
    const segmentStartAt = segment.exp.startedAt ?? segment.lastContinuousAt
    if (state.capturedAt - segmentStartAt >= RANK_MAXIMUM_SEGMENT_MS) {
      const completed = this.finish(segment.lastContinuousAt)
      if (this.mapIsFresh(state.capturedAt, mapName, mapObservedAt) && levelBucket !== null) {
        this.startSegment(state, accounting, mapName, levelBucket, mapObservedAt!)
        this.observeExp(accounting)
      }
      return completed
    }

    if (levelBucket === null) return this.finish(segment.lastContinuousAt)

    if (accounting.exp.continuity === 'rebaseline') {
      const completed = this.finish(segment.lastContinuousAt)
      if (this.mapIsFresh(state.capturedAt, mapName, mapObservedAt)) {
        this.startSegment(state, accounting, mapName, levelBucket, mapObservedAt!)
        this.observeExp(accounting)
      }
      return completed
    }

    const recognitionGapMs = Math.max(0, state.capturedAt - segment.lastMapObservedAt)
    if (recognitionGapMs > RANK_RECOGNITION_GRACE_MS) {
      const completed = this.finish(segment.lastContinuousAt)
      if (this.mapIsFresh(state.capturedAt, mapName, mapObservedAt)) {
        this.startSegment(state, accounting, mapName, levelBucket, mapObservedAt!)
        this.observeExp(accounting)
      }
      return completed
    }

    const hasNewMapObservation = mapObservedAt !== null && mapObservedAt > segment.lastMapObservedAt
    if ((hasNewMapObservation && mapName !== segment.mapName) || levelBucket !== segment.levelBucket) {
      const completed = this.finish(segment.lastContinuousAt)
      if (this.mapIsFresh(state.capturedAt, mapName, mapObservedAt)) {
        this.startSegment(state, accounting, mapName, levelBucket, mapObservedAt!)
        this.observeExp(accounting)
      }
      return completed
    }

    if (hasNewMapObservation && mapName === segment.mapName) {
      segment.lastMapObservedAt = mapObservedAt
      if (segment.recognitionPaused) {
        segment.recognitionPaused = false
        segment.exp.last = null
      }
    }
    if (state.jobName) segment.jobName = state.jobName

    const mapAgeMs = Math.max(0, state.capturedAt - segment.lastMapObservedAt)
    if (!mapName || mapName !== segment.mapName || mapAgeMs > RANK_MAP_FRESHNESS_MS) {
      this.pauseRecognition()
      return null
    }

    segment.lastContinuousAt = state.capturedAt
    this.observeAccounting(accounting)
    return null
  }

  finish(now = Date.now()): RankSubmissionDraft | null {
    const segment = this.segment
    this.segment = null
    const exp = segment?.exp
    if (!segment || !exp?.start || !exp.end || exp.startedAt === null || exp.endedAt === null) return null
    const endedAt = Math.max(exp.startedAt, Math.min(now, exp.endedAt))
    if (endedAt - exp.startedAt < RANK_MINIMUM_DURATION_MS
      || exp.trustedDurationMs < RANK_MINIMUM_DURATION_MS
      || exp.gain <= 0) return null

    const job = matchRankJob(segment.jobName)
    return {
      submissionId: crypto.randomUUID(),
      presetId: RANK_PRESET_ID,
      presetVersion: this.presetVersion,
      mapName: segment.mapName,
      levelBucket: segment.levelBucket,
      ...(job ? { jobKey: job.jobKey, jobName: job.jobName } : {}),
      startedAt: exp.startedAt,
      endedAt,
      exp: {
        gain: exp.gain,
        trustedDurationMs: exp.trustedDurationMs,
        start: exp.start,
        end: exp.end,
      },
    }
  }

  private startSegment(
    state: GameStateSnapshot,
    accounting: AccountingFrame,
    mapName: string,
    levelBucket: number,
    mapObservedAt: number,
  ): void {
    this.segment = {
      mapName,
      levelBucket,
      lastContinuousAt: state.capturedAt,
      lastMapObservedAt: mapObservedAt,
      recognitionPaused: false,
      captureGeneration: accounting.captureGeneration,
      jobName: state.jobName ?? null,
      exp: emptyExp(),
    }
  }

  private observeAccounting(accounting: AccountingFrame): void {
    if (!this.segment) return
    if (this.segment.captureGeneration !== accounting.captureGeneration) {
      this.segment.captureGeneration = accounting.captureGeneration
      this.segment.exp.last = null
    }
    this.observeExp(accounting)
  }

  private observeExp(accounting: AccountingFrame): void {
    if (!this.segment) return
    const update = accounting.exp
    const metric = this.segment.exp
    if (update.continuity === 'ignored' || update.continuity === 'pending') return
    if (update.continuity === 'unavailable' || update.continuity === 'discontinuous') {
      metric.last = update.end
      if (update.end && rankLevelBucket(update.end.level) === this.segment.levelBucket) {
        this.seedExp(metric, update.end, update.sourceEndAt ?? update.sourceAt ?? accounting.capturedAt)
      }
      return
    }
    if (!update.exact || !update.end || rankLevelBucket(update.end.level) !== this.segment.levelBucket) return
    if (!metric.last || update.continuity === 'baseline' || !update.start) {
      this.seedExp(metric, update.end, update.sourceEndAt ?? update.sourceAt ?? accounting.capturedAt)
      return
    }
    if (rankLevelBucket(update.start.level) !== this.segment.levelBucket
      || !this.sameExpPoint(metric.last, update.start)
      || update.delta === null
      || update.delta < 0) {
      this.seedExp(metric, update.end, update.sourceEndAt ?? update.sourceAt ?? accounting.capturedAt)
      return
    }
    metric.gain += update.delta
    metric.trustedDurationMs += Math.max(0, update.trustedIntervalMs)
    metric.last = update.end
    metric.end = update.end
    metric.endedAt = update.sourceEndAt ?? update.sourceAt ?? accounting.capturedAt
  }

  private seedExp(metric: ExpAccumulator, point: RankExpPoint, capturedAt: number): void {
    metric.start ??= point
    metric.startedAt ??= capturedAt
    metric.end = point
    metric.last = point
    metric.endedAt = capturedAt
  }

  private pauseRecognition(): void {
    if (!this.segment || this.segment.recognitionPaused) return
    this.segment.recognitionPaused = true
    this.segment.exp.last = null
  }

  private mapIsFresh(capturedAt: number, mapName: string, mapObservedAt: number | null): boolean {
    return Boolean(mapName && mapObservedAt !== null && capturedAt - mapObservedAt <= RANK_MAP_FRESHNESS_MS)
  }

  private sameExpPoint(left: RankExpPoint, right: RankExpPoint): boolean {
    return left.level === right.level && left.absolute === right.absolute
  }
}
